import { Add } from '@mui/icons-material';
import { Box, Button, LinearProgress, Tab, Tabs, Typography } from '@mui/material';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ToolsDamInformation, TableSimulation, FilesTable } from '../../../../../../components/custom';
import { CustomAlert, VMenu } from '../../../../../../components/shared';
import { LayoutBase } from '../../../../../../layouts';
import { SummarySimulation } from '../../../../../../components/custom/ActionPlans/SummarySimulation';
import { EmegencySimulationsServices, IEmergencySimulation } from '../../../../../../services/api/action-plan/EmergencySimulation';
import { useAuthContext } from '../../../../../../context';
import { IFileServices, FileServicesBack } from '../../../../../../services/api/files/FileServicesBack';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component='div'>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function tabProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const EmergencySituationSimulation = () => {

  //Tabs
  const [value, setValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const { userData } = useAuthContext();
  const url = useParams();
  const [data, setData] = useState<IEmergencySimulation[]>([]);
  const [lastPae, setLastPae] = useState<IEmergencySimulation | null>();
  const navigate = useNavigate();
  const [dataFilter, setDataFilter] = useState<IFileServices[]>([]);
  const [dataFiles, setDataFiles] = useState<IFileServices[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loadin, setLoadin] = useState<boolean>(false);

  const simulationPermissionsString: string | undefined = useMemo(() => {
    return userData?.nivel_acesso?.PAE?.simulado;
  }, [userData]);

  const hasSimulationPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
    if (typeof simulationPermissionsString === 'string' && simulationPermissionsString !== '') {
      return simulationPermissionsString.includes(action);
    }
    return false;
  }, [simulationPermissionsString]);

  useEffect(() => {
    setLoadin(true);
    fetchData();
    fetchDataFiles();
  }, [url.id]);

  const fetchData = () => {
    if (url.id) {
      EmegencySimulationsServices.list(url.id)
        .then((response) => {
          if (response instanceof Error) {
            console.log(response.message);
          } else {
            setData(response);
            if (response.length !== 0) {
              const lastUuid = getMostRecentUuid(response);
              getPae(lastUuid);
              setLoadin(false);
            }
            setLoadin(false);
          }
        });
    }
  };

  const handleDelete = () => {
    fetchData();
  };

  const getPae = (uuid: string | null) => {
    if (uuid !== null) {
      EmegencySimulationsServices.get(uuid)
        .then((response) => {
          if (response instanceof Error) {
            console.log(response.message);
          } else {
            setLastPae(response);
          }
        });
    }
  };

  const getMostRecentUuid = (data: IEmergencySimulation[]): string | null => {
    const sortedData = data.sort((a, b) => new Date(b.data_simulado).getTime() - new Date(a.data_simulado).getTime());
    return sortedData[0].uuid ? sortedData[0].uuid : null;
  };

  const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setAlertInfo({ open: false, message: '', severity: 'info' });
    setTimeout(() => {
      setAlertInfo({ open: true, message, severity });
    }, 100);
  };

  async function handleDeleteFile(valueId: string | undefined) {
    if (valueId !== undefined) {
      FileServicesBack.deleteById(valueId)
        .then((result) => {
          if (result instanceof Error) {
            showAlert('Não foi possível excluir o documento! ' + result.message, 'error');
          } else {
            showAlert('Documento excluído!', 'success');
            fetchDataFiles();
            setDialogOpen(false);
          }
        });
    }
  }

  function handleFileType(newValue: string[] | null) {
    if (newValue !== null) {
      if (newValue.length > 0) {
        if (dataFilter) {
          const filteredData = dataFilter.filter(index => {
            const tipo = index.tipo?.tipo || '';
            const normalizedTipo = tipo.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
            return newValue.some(value => {
              const normalizedValue = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
              return normalizedTipo.includes(normalizedValue);
            });
          });
          setDataFilter(filteredData);
        }
      } else {
        setDataFilter(dataFiles);
      }
    } else {
      setDataFilter(dataFiles);
    }
  }

  async function sortedData() {
    if (dataFilter) {
      const sorted = dataFilter.slice().sort((a, b) => {
        if (a.data_emissao && b.data_emissao) {
          const dateA = new Date(a.data_emissao).getTime();
          const dateB = new Date(b.data_emissao).getTime();
          if (sortDirection === 'asc') {
            setSortDirection('desc');
            return dateA - dateB;
          } else {
            setSortDirection('asc');
            return dateB - dateA;
          }
        }
        return 0;
      });
      setDataFilter(sorted);
    }
  }

  const toggleDialog = () => {
    setDialogOpen(!isDialogOpen);
  };

  function handleEditFile(key: string | undefined) {
    if (key !== undefined) {
      navigate(`/information_dam/${url.dam}/files/file/${key}`);
    }
  }

  const fetchDataFiles = () => {
    if (url.id) {
      FileServicesBack.get(url.id)
        .then((result) => {
          if (result instanceof Error) {
            setAlertInfo({ open: true, message: `Erro ao buscar arquivos! error: ${result.message}`, severity: 'info' });
          } else {
            const value = result.filter(index => index.tipo.tipo === 'Relatório dos simulados de emergência');
            setDataFiles(value);
          }
        });
    }
  };

  return (
    <LayoutBase
      tollBar={<ToolsDamInformation />}
      title='Simulado de Situação de Emergência'
    >
      <Box flex={1}>
        <VMenu id={url.id} />
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }} justifyContent={'space-around'} display={'flex'}>
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons allowScrollButtonsMobile>
            <Tab label="Informações do Simulado" {...tabProps(0)} />
            <Tab label="HISTÓRICO" {...tabProps(1)} />
            {/* <Tab label="ARQUIVOS" {...tabProps(2)} /> */}
          </Tabs>
        </Box>

        <CustomTabPanel value={value} index={0}>
          <Box display={'flex'} justifyContent={'end'} marginBottom={1}>
            {hasSimulationPermission('add') &&
              <Button
                onClick={() => navigate('./detalhes/novo')}
                variant='contained'
                sx={{ borderRadius: '25px' }}
                startIcon={<Add />}
              >
                Novo
              </Button>
            }
          </Box>
          {loadin ? <LinearProgress /> :
            lastPae == undefined ? null : <SummarySimulation data={lastPae} />
          }
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          <TableSimulation data={data} onDelet={handleDelete} permissions={simulationPermissionsString} />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={2}>
          <FilesTable
            dataFilter={dataFiles}
            handleDelete={handleDeleteFile}
            handleFileType={handleFileType}
            sortedData={sortedData}
            url={url}
            title='Arquivos Simulado de Situação de Emergência '
            isDialogOpen={isDialogOpen}
            toggleDialog={toggleDialog}
            handleEditFile={handleEditFile}
            type='Simulado de Situação de Emergência '
            showAdd={hasSimulationPermission('add')} 
            pagePermissions={simulationPermissionsString}
          />
        </CustomTabPanel>
      </Box>
      {alertInfo.open &&
        <CustomAlert
          message={alertInfo.message}
          severity={alertInfo.severity}
        />
      }
    </LayoutBase>
  );
};
